<script setup lang="ts">
  import { computed, watchEffect, type Component } from 'vue'
  import { useScrollLock } from '@vueuse/core'
  import {
    getModalRegistration,
    getRegisteredModalKinds,
    MODAL_STACK_BASE,
    useModalStack,
    type ModalParams
  } from '../../lib/modal-stack'
  import ModalStackSlot from './ModalStackSlot.vue'

  // The single host for stacked overlays (central-modal-host plan, 1.3). Mount
  // once at the app shell. It renders every registered persistent kind and
  // assigns each a z = its position in the ordered stack, so z is a pure
  // projection of one ordered array (no per-modal global-counter race).
  //
  // Phase 1 renders all registered persistent kinds (their content stays lazy
  // via the inner Modal's Presence gate, exactly as the hand-mounted providers
  // did — no eager fetch). The "mount only open entries" refinement lands in
  // Phase 2, when the stack takes over open-state from the per-feature query
  // watchers.

  interface RenderedEntry {
    key: string
    component: Component
    params: ModalParams
    zIndex: number
    isTop: boolean
  }

  const stack = useModalStack()

  // Host modals render non-modal (the stack — not reka — owns interaction), so
  // reka no longer locks body scroll. Restore it here while any entry is open.
  // Overflow-only (not body `pointer-events: none`), so the non-modal content
  // stays interactive; reka still locks for transient confirms opened within a
  // modal, and that pointer-events lock correctly makes the modal inert beneath
  // the confirm for as long as it's open.
  const bodyScrollLocked = useScrollLock(
    typeof document === 'undefined' ? null : document.body
  )
  watchEffect(() => {
    bodyScrollLocked.value = stack.entries.value.length > 0
  })

  const rendered = computed<RenderedEntry[]>(() => {
    const order = stack.entries.value
    const lastIndex = order.length - 1
    const result: RenderedEntry[] = []
    for (const kind of getRegisteredModalKinds()) {
      const registration = getModalRegistration(kind)
      if (!registration || !registration.persist) continue
      const index = order.findIndex((entry) => entry.kind === kind)
      const entry = index >= 0 ? order[index] : undefined
      result.push({
        key: kind,
        component: registration.component,
        params: entry?.params ?? {},
        // Open entries take z by stack index; a closed kind sits below the
        // band (its Modal renders no content, so the value is inert).
        zIndex: index >= 0 ? MODAL_STACK_BASE + index : MODAL_STACK_BASE - 1,
        // The top entry owns the dim backdrop + interaction; buried entries
        // render non-interactive behind it. A closed kind is never top.
        isTop: index >= 0 && index === lastIndex
      })
    }
    return result
  })
</script>
<template>
  <ModalStackSlot
    v-for="item in rendered"
    :key="item.key"
    :component="item.component"
    :params="item.params"
    :z-index="item.zIndex"
    :is-top="item.isTop"
  />
</template>
